import { useEffect, useRef, useState } from "react";

export const ImageSlider = ({
  images,
  active,
  setActive,
  parts = 8,
  interval = 7_500,
  speed = 50,
  className = "",
  onChange,
  style = {},
  onLoad,
  children,
  ...rest
}) => {
  const intervalRef = useRef();
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);

  const [renderTime] = useState(Date.now());

  const goNext = () => {
    setActive((prev) => (prev + 1) % images.length);
  };

  const goPrev = () => {
    setActive((prev) => (prev - 1 + images.length) % images.length);
  };

  const resetInterval = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(goNext, interval);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = true;
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    const minSwipeDistance = 50;
    const isHorizontalSwipe = Math.abs(diffX) > Math.abs(diffY);

    if (isHorizontalSwipe && Math.abs(diffX) > minSwipeDistance) {
      if (diffX > 0) {
        goNext();
      } else {
        goPrev();
      }

      resetInterval();
    }

    isSwiping.current = false;
  };

  useEffect(() => {
    onChange?.(active);
    resetInterval();
  }, [active]);

  useEffect(() => {
    resetInterval();

    (async ()=>{
      const loadingImages = images.map((url)=>{
        return new Promise((resolve)=>{
          const img = new Image();
          img.src = url;
          img.onload = resolve;
        })
      });

      await Promise.all(loadingImages);
      
      for (let i = 0; i < images.length; i++){
        const url = images[i];
        const slider = containerRef.current.children[i];
        const innerParts = slider.querySelectorAll('.slide-inner');
        for (const el of innerParts){
          el.style.backgroundImage = `url("${url}")`;
        }
      }

      onLoad?.();
    })();

    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  const containerRef = useRef();

  return (
    <div
      className={`image-slider overflow-hidden touch-pan-y ${className}`}
      style={style}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      {...rest}
    >
      {images.map((imageUrl, i) => (
        <div
          className={`slider-container absolute inset-0 flex ${
            active === i ? "activeSlide" : ""
          }`}
          key={`slider_container_${renderTime}_${i}`}
          style={{
            zIndex: active === i ? 1 : 0,
          }}
        >
          {Array.from({ length: parts }).map((_, x) => (
            <div
              className="slide-part block h-full"
              key={`slider_part_${renderTime}_${i}_${x}`}
              style={{
                width: `${(1 / parts) * 100}%`,
              }}
            >
              <div
                className="
                  slide-inner block h-full w-full overflow-hidden bg-no-repeat
                  transition-transform duration-[500ms] ease-in-out relative
                "
                style={{
                  backgroundSize: "0 0",
                  zIndex: 1000 - x,
                  transform: `translateX(${
                    active === i ? "0" : `-${(x + 1) * 66}%`
                  })`,
                  transitionDelay: `${(x + 1) * speed}ms`,
                }}
              />
            </div>
          ))}
        </div>
      ))}

      <div className="dotContainer absolute bottom-0 p-6 inset-x-0 flex justify-start z-[6]">
        <div className="flex gap-6 items-center">
          {Array.from({ length: images.length }).map((_, i) => (
            <button
              key={i}
              onClick={() => {
                window.scrollTo(0, 0);
                setActive(i);
                resetInterval();
              }}
              className="relative flex items-center justify-center"
            >
              <span
                className={`
                  dot rounded-full transition-all duration-150
                  ${
                    active === i
                      ? "dotActive pulse-ring h-3 w-3 bg-blue-600"
                      : "h-2.5 aspect-square bg-neutral-50/50 hover:bg-neutral-50/75"
                  }
                `}
              />
            </button>
          ))}
        </div>
      </div>

      <style>{`
        .slide-inner:before {
          content: "";
          position: absolute;
          width: calc(100vw + 2px);
          height: 100%;
          background-image: inherit;
          background-size: cover;
          background-position: center center;
          transition-property: opacity,left;
          transition-duration: 750ms,500ms;
          transition-delay: 375ms,250ms;
          opacity: 0;
        }

        .activeSlide .slide-inner:before {
          opacity: 1;
        }

        ${Array.from({ length: parts })
          .map(
            (_, i) => `
            .slide-part:nth-child(${i + 1}) .slide-inner:before {
              left: ${i * -100}%;
            }
          `
          )
          .join("\n")}
        
        .image-slider:after {
          content: "";
          z-index: 5;
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.25);
          pointer-events: none;
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }

        .pulse-ring:before {
          content: "";
          position: absolute;
          inset: 0;
          border: 1px solid #2563eb;
          border-radius: 9999px;
          animation: pulse-ring 1.5s ease-out infinite;
        }
      `}</style>

      {children}
    </div>
  );
};